import URL from 'src/api/url/api'

/**
 * Links to a learning path or course, in two contexts:
 * - "portal": opened inside the workspace portal (portal header and navigation)
 *     share host:    share…/portal/paths/{pathId}
 *                    share…/portal/paths/{pathId}/{courseCode}   (a course in a path)
 *                    share…/portal/courses/{courseCode}          (a course in no path)
 *     portal domain: {domain}/paths/{pathId}, {domain}/paths/{pathId}/{courseCode},
 *                    {domain}/courses/{courseCode}
 * - "direct": the content on its own (emails, campaigns, widgets, embeds), unchanged:
 *     the path's / course's own custom domain, else share…/category/… or share…/{code}
 */
export const LINK_CONTEXTS = {
  PORTAL: 'portal',
  DIRECT: 'direct',
}

const shareOrigin = () =>
  (URL.APP_URL || 'https://share.minicoursegenerator.com').replace(/\/+$/, '')

const slugify = name =>
  (name || '')
    .replace(/ /g, '')
    .replace(/ /g, '-')
    .replace(/[?&#%]/g, '')

/** A custom domain mapped to the item opens it at the domain root. */
const isRootDomain = (domain, defaultDomain) => domain !== defaultDomain

/**
 * Where portal links point for a workspace:
 * - home:        the portal itself (share…/portal/{shopKey} or its custom domain)
 * - contentBase: prefix of path/course links (share…/portal or the custom domain)
 */
export const getPortal = ({ domain, shopKey }) => {
  if (domain) {
    const origin = `https://${domain}`
    return { home: origin, contentBase: origin, domain }
  }
  return {
    home: shopKey ? `${shareOrigin()}/portal/${shopKey}` : null,
    contentBase: `${shareOrigin()}/portal`,
    domain: null,
  }
}

/**
 * `root`: whether `domain` opens this path at its root. Omitted, any custom domain is assumed to
 * (callers that only list the path's own domains); pass false for a workspace domain whose
 * default is something else, so the link uses /category/….
 */
export const getPathLink = ({
  context,
  domain,
  defaultDomain,
  path,
  portal,
  root,
}) => {
  if (!path) return null
  if (context === LINK_CONTEXTS.PORTAL && portal) {
    return `${portal.contentBase}/paths/${path.id}`
  }
  if (root ?? isRootDomain(domain, defaultDomain)) return `https://${domain}`
  return `https://${domain}/category/${slugify(path.name)}-1/${path.id}`
}

/** `path` is the course's learning path ({ id }), if it is in one. */
export const getCourseLink = ({
  context,
  domain,
  defaultDomain,
  course,
  portal,
  path,
}) => {
  if (!course || !course.code) return null
  if (context === LINK_CONTEXTS.PORTAL && portal) {
    return path
      ? `${portal.contentBase}/paths/${path.id}/${course.code}`
      : `${portal.contentBase}/courses/${course.code}`
  }
  if (isRootDomain(domain, defaultDomain)) return `https://${domain}`
  return `https://${domain}/${course.code}`
}
